import { useDebounce } from "@lyfegen-platform/react-common";
import { Box, IconButton, MenuItem, TextField, Typography } from "@material-ui/core";
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from "@material-ui/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import {
	agreementsActivePage,
	agreementsRowsPerPage,
	publicAgreementsActivePage,
	publicAgreementsRowsPerPage,
} from "../../store/agreements";
import { casesActivePage, casesRowsPerPage } from "../../store/cases";
import {
	formsActivePage,
	formsRowsPerPage,
	questsActivePage,
	questsRowsPerPage,
	recordsActivePage,
	recordsRowsPerPage,
} from "../../store/data";
import { documentsActivePage, documentsRowsPerPage } from "../../store/documents";
import { refundsActivePage, refundsRowsPerPage } from "../../store/refunds";
import { simulationsActivePage, simulationsRowsPerPage } from "../../store/simulator";
import MappedComponent from "../mapped-components/MappedComponent";
import { TableTypes } from "./LyfeDataTable";
import useStyles from "./use-styles";

interface IProps {
	count: number;
	totalCount?: number;
	pageSizeOptions: Array<number>;
	title: TableTypes;
	isGroups?: boolean;
}

type ArrowTypes = "first" | "last" | "next" | "previous";

function getActivePageState(type: TableTypes) {
	switch (type) {
		case "records":
			return recordsActivePage;
		case "agreements":
			return agreementsActivePage;
		case "publicAgreements":
			return publicAgreementsActivePage;
		case "cases":
			return casesActivePage;
		case "transactions":
			return refundsActivePage;
		case "documents":
			return documentsActivePage;
		case "quests":
			return questsActivePage;
		case "simulations":
			return simulationsActivePage;
		default:
			return formsActivePage;
	}
}

function getActiveRowsPerPageState(type: TableTypes) {
	switch (type) {
		case "records":
			return recordsRowsPerPage;
		case "agreements":
			return agreementsRowsPerPage;
		case "cases":
			return casesRowsPerPage;
		case "transactions":
			return refundsRowsPerPage;
		case "publicAgreements":
			return publicAgreementsRowsPerPage;
		case "documents":
			return documentsRowsPerPage;
		case "quests":
			return questsRowsPerPage;
		case "simulations":
			return simulationsRowsPerPage;
		default:
			return formsRowsPerPage;
	}
}

export default function UsePagination(props: IProps) {
	const { t } = useTranslation();
	const classes = useStyles({});
	const [activePage, setActivePage] = useRecoilState(getActivePageState(props.title));
	const [displayedActivePage, setDisplayedActivePage] = useState("");
	const [activeRowsPerPage, setActiveRowsPerPage] = useRecoilState(getActiveRowsPerPageState(props.title));
	const availablePages = Math.ceil(props.count / activeRowsPerPage);
	const debouncedPage = useDebounce(displayedActivePage);

	useEffect(() => {
		setDisplayedActivePage(`${activePage + 1}`);
	}, []);

	useEffect(() => {
		if (+displayedActivePage > 0 && availablePages > 0) {
			const isFirstPage = +displayedActivePage === 1;
			const sanitizedActivePage = isFirstPage ? 0 : Math.min(Math.max(+displayedActivePage - 1, 0), availablePages - 1);
			setActivePage(+sanitizedActivePage);
		}
	}, [debouncedPage]);

	function getIcon(type: ArrowTypes | string) {
		switch (type) {
			case "first":
				return <FirstPage />;
			case "last":
				return <LastPage />;
			case "next":
				return <ChevronRight />;
			case "previous":
				return <ChevronLeft />;
			default:
				return null;
		}
	}

	function getPageNumber(i: ArrowTypes) {
		switch (i) {
			case "first":
				return 1;
			case "last":
				return availablePages;
			case "next":
				return +displayedActivePage + 1;
			case "previous":
				return +displayedActivePage - 1;
			default:
				return 0;
		}
	}

	function isDisabled(i: ArrowTypes, page: number) {
		switch (i) {
			case "first":
				return page === 0 ? true : false;
			case "previous":
				return page === 0 ? true : false;
			case "next":
				return page === 0 && availablePages === 0 ? true : page === availablePages - 1 ? true : false;
			case "last":
				return page === 0 && availablePages === 0 ? true : page === availablePages - 1 ? true : false;
			default:
				return true;
		}
	}

	function handleRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
		setActiveRowsPerPage(+event.target.value);
	}

	function handleChangePage(event: React.ChangeEvent<HTMLInputElement>, page?: number) {
		const val = event.target.value;
		const sanitizedDisplayActivePage = +val > availablePages ? availablePages : val;
		setDisplayedActivePage(`${page ?? sanitizedDisplayActivePage}`);
	}

	return (
		<ul className={classes.ul}>
			<Box display="flex" alignItems="center">
				<Typography variant="body2">{props.isGroups ? t("groups-per-page") : t("rows-per-page")}:</Typography>
				&nbsp;
				<TextField select value={activeRowsPerPage} onChange={handleRowsPerPage} variant="outlined" size="small">
					{props.pageSizeOptions.map((option, i) => (
						<MenuItem key={i} value={option} disabled={props.pageSizeOptions[i - 1] > props.count}>
							<Typography variant="body2">{option}</Typography>
						</MenuItem>
					))}
				</TextField>
			</Box>
			<Box display="flex" alignItems="center">
				<Typography variant="body2">{t("page")}</Typography>
				&nbsp;
				<TextField
					type="number"
					value={displayedActivePage}
					onChange={handleChangePage}
					variant="outlined"
					InputProps={{
						classes: {
							input: classes.textInput,
						},
						inputProps: { min: 1, max: availablePages },
					}}
					size="small"
					className={classes.pageInput}
					error={activePage && availablePages ? activePage > availablePages : false}
				></TextField>
				&nbsp;
				<Typography variant="body2">{t("of")}</Typography>
				&nbsp;
				<Typography variant="body2">{availablePages || 1}</Typography>
			</Box>
			<Box display="flex" alignItems="center">
				{(["first", "previous", "next", "last"] as Array<ArrowTypes>).map((i) => {
					const props = {
						title: t(i),
						children: (
							<span>
								<IconButton
									disabled={isDisabled(i, activePage)}
									size="small"
									onClick={(_) => handleChangePage(_ as any, getPageNumber(i))}
								>
									{getIcon(i as ArrowTypes)}
								</IconButton>
							</span>
						),
					};
					return <MappedComponent key={i} component="Tooltip" props={props} />;
				})}
			</Box>
		</ul>
	);
}
